// typeCreatePolicy.js — whether a type supports blank / top-level create

/**
 * Types that are ingest/import sourced by default (no blank "+ Add" create).
 * Explicit `allowManualCreate` on the type doc overrides this default.
 */
export const DEFAULT_INGEST_ONLY_TYPE_KEYS = Object.freeze(new Set([
  'COMMENT',
]));

/**
 * Types that cannot be created from the space root by default.
 * Explicit `allowTopLevelCreate` on the type doc overrides this default.
 * These remain creatable from a parent (related / line-item flows).
 * Does not decide Menu or + Add visibility — see typeDisplayPolicy.js.
 */
export const DEFAULT_CHILD_ONLY_TYPE_KEYS = Object.freeze(new Set([
  'PROPOSAL_ITEM',
  'PURCHASE_ORDER_ITEM',
  'PURCHASE_ORDER_PAYMENT',
  'INVOICE_ITEM',
  'DOCUMENT_PAYMENT',
]));

/**
 * resolveTypeKey
 *
 * @param {object|string|null|undefined} typeDefOrKey - Type def or type key.
 * @returns {string} Uppercase type key, or empty string.
 */
function resolveTypeKey(typeDefOrKey) {
  return String(
    (typeof typeDefOrKey === 'string' ? typeDefOrKey : typeDefOrKey?.key)
    || '',
  ).trim().toUpperCase();
}

/**
 * resolveTypeDef
 *
 * @param {object|string|null|undefined} typeDefOrKey - Type def or type key.
 * @returns {object|null}
 */
function resolveTypeDef(typeDefOrKey) {
  return typeof typeDefOrKey === 'object' && typeDefOrKey
    ? typeDefOrKey
    : null;
}

/**
 * typeAllowsManualCreate
 *
 * Whether the UI should offer blank create for this type (collection Add,
 * Add Modal create row, empty-state create, etc.).
 *
 * @param {object|string|null|undefined} typeDefOrKey - Type def or type key.
 * @returns {boolean} True when blank create is allowed.
 */
export function typeAllowsManualCreate(typeDefOrKey) {
  const typeKey = resolveTypeKey(typeDefOrKey);
  const typeDef = resolveTypeDef(typeDefOrKey);

  if (typeDef && Object.prototype.hasOwnProperty.call(typeDef, 'allowManualCreate')) {
    return typeDef.allowManualCreate !== false;
  }

  if (!typeKey) return true;
  return !DEFAULT_INGEST_ONLY_TYPE_KEYS.has(typeKey);
}

/**
 * typeAllowsTopLevelCreate
 *
 * Whether this type can be created from the space with no parent.
 * Independent of Menu / + Add visibility and of parent-scoped create pickers.
 *
 * @param {object|string|null|undefined} typeDefOrKey - Type def or type key.
 * @returns {boolean} True when top-level create is allowed.
 */
export function typeAllowsTopLevelCreate(typeDefOrKey) {
  const typeKey = resolveTypeKey(typeDefOrKey);
  const typeDef = resolveTypeDef(typeDefOrKey);

  if (typeDef && Object.prototype.hasOwnProperty.call(typeDef, 'allowTopLevelCreate')) {
    return typeDef.allowTopLevelCreate !== false;
  }

  if (!typeKey) return true;
  return !DEFAULT_CHILD_ONLY_TYPE_KEYS.has(typeKey);
}
